import {
  ActionMenu,
  BasicTable,
  ConfirmDialog,
  Notification,
  PageTopBar,
  PageView,
  SearchInput,
  CreateLocationModal,
  SimulatePutawayModal,
} from '../../components'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from 'react-query'
import { UserContext } from '../../hooks/UserContext'
import { Link } from 'react-router-dom'
import { useHistory, useParams } from 'react-router-dom'
import { Fragment, useRef, useState, useContext, useMemo } from 'react'
import { Loader } from '../../components/Loader'
import { ButtonLoader } from '../../components'
import { ExpandibleRow } from '../../components/BasicTable/ExpandibleRow'
import { getSkus } from '../../utils/productUtils'
import { isWarehouseAdminUser } from '../../utils/userUtils'
import {
  exportWarehouseLocations,
  fetchWarehouseLocations,
  enqueueLocationsExport,
  getLocationsExport,
  fetchLocationContainerCounts,
} from '../../services/locationServices'
import { FoundStockHubButton } from './FoundStockHubButton'
import { CoverageEventsBadge } from './CoverageEventsBadge'
import { TotalCountContainer } from './TotalCountContainer'
import { LocationContainersPanel } from './LocationContainersPanel'
import { SelectColumnFilter } from '../../components/BasicTable/modules/ColumnFiltersUI'
import { LOCATION_TYPES } from './LocationTypes'
import { INVENTORY_HISTORY, VIEW_WAREHOUSE_LOCATIONS, LOCATIONS, DELETE_LOST_STOCK, CYCLE_COUNTS } from '../../navigation/constants'
import { setLocationsListPageSize } from '../../redux/pageSizeSlice'
import { useDispatch, useSelector } from 'react-redux'
import { CheckCircleIcon, DownloadIcon, TrashIcon, UploadIcon } from '@heroicons/react/outline'
import { TableImage } from '../../components/TableImage'

function getName(product) {
  if (product.is_dropshipping) {
    return (
      <>
        {product.name}
        <br></br>
        <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 bg-red-100 text-red-800'}>Dropshipping</span>
      </>
    )
  } else if (product.is_kit) {
    return (
      <>
        {product.name}
        <br></br>
        <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 bg-blue-100 text-blue-800'}>Kit</span>
      </>
    )
  } else {
    return product.name
  }
}

const qty = (count, one, other) => `${count} ${count === 1 ? one : other}`

const ContainersCell = ({ counts, onOpen, t }) => {
  const boxCount = counts?.box_count || 0
  const palletCount = counts?.pallet_count || 0
  if (boxCount === 0 && palletCount === 0) return null
  const boxesText = qty(boxCount, t('list_locations.containers_cell.box_one'), t('list_locations.containers_cell.box_other'))
  const palletsText = qty(palletCount, t('list_locations.containers_cell.pallet_one'), t('list_locations.containers_cell.pallet_other'))
  let text
  if (boxCount > 0 && palletCount > 0) {
    text = `${boxesText} ${t('list_locations.containers_cell.in')} ${palletsText}`
  } else if (boxCount > 0) {
    text = boxesText
  } else {
    text = palletsText
  }
  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <div className="text-sm text-gray-700">{text}</div>
      <button className="text-indigo-600 hover:text-indigo-900 text-xs" onClick={onOpen}>
        {t('list_locations.containers_cell.view_detail')}
      </button>
    </div>
  )
}

export const ListLocationsContainer = () => {
  const history = useHistory()
  const { user } = useContext(UserContext)
  const [loadingButton, setLoadingButton] = useState(false)
  const { warehouse_id } = useParams()
  const dispatch = useDispatch()
  const pageSize = useSelector(state => state.pageSize.locationsList)
  const [searchParams, setSearchParams] = useState({
    page: 1,
    per_page: pageSize,
  })
  const { i18n } = useTranslation()
  const tableRef = useRef()
  const queryClient = useQueryClient()

  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [enqueueingExport, setEnqueueingExport] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [exportNotificationOpen, setExportNotificationOpen] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [exportFileUrl, setExportFileUrl] = useState(null)
  const [isCreateLocationModalOpen, setIsCreateLocationModalOpen] = useState(false)
  const [isSimulatePutawayModalOpen, setIsSimulatePutawayModalOpen] = useState(false)
  const [containersPanel, setContainersPanel] = useState(null)

  const permissions = useMemo(() => {
    return {
      showInventoryHistory: user ? user.permissions.index_inventory_history : false,
      deleteLostStock: user ? user.permissions.delete_lost_stock : false,
      manageCycleCounts: user ? user.permissions.manage_cycle_counts : false,
      canPutawayToLocations: user ? isWarehouseAdminUser(user) : false,
    }
  }, [user])

  const wrapWithIndigoSpan = text => <span className="text-indigo-600 text-base">{text}</span>

  const locationToExportDescription = location => <>{i18n.t('list_locations.location_to_export_description', { location: wrapWithIndigoSpan(location) })}</>
  const productSkuToExportDescription = sku => <>{i18n.t('list_locations.sku_to_export_description', { sku: wrapWithIndigoSpan(sku) })}</>
  const productUpcToExportDescription = upc => <>{i18n.t('list_locations.upc_to_export_description', { upc: wrapWithIndigoSpan(upc) })}</>
  const storeToExportDescription = store => <>{i18n.t('list_locations.store_to_export_description', { store: wrapWithIndigoSpan(store) })}</>
  const locationTypeToExportDescription = type => <>{i18n.t('list_locations.location_type_to_export_description', { type: wrapWithIndigoSpan(type) })}</>
  const productNameToExportDescription = productName => (
    <>{i18n.t('list_locations.product_name_to_export_description', { productName: wrapWithIndigoSpan(productName) })}</>
  )

  const ordersExportDescriptionBuilders = {
    location_code: locationToExportDescription,
    store: storeToExportDescription,
    location_type: locationTypeToExportDescription,
    product_name: productNameToExportDescription,
    sku: productSkuToExportDescription,
    upc: productUpcToExportDescription,
  }

  const buildExportDescription = function (tableFilters) {
    if (!tableFilters || !Object.keys(tableFilters).length)
      return (
        <p className="text-sm text-gray-500">Se exportaran todos los productos del almacén. Si deseas, usa los filtros para obtener un reporte específico.</p>
      )
    //console.log("tableFilters", tableFilters)
    const filtersDescriptions = Object.keys(tableFilters)
      .filter(filterKey => ordersExportDescriptionBuilders[filterKey] instanceof Function)
      .map(filterKey => ordersExportDescriptionBuilders[filterKey](tableFilters[filterKey]))
    //console.log("filtersDescriptions", filtersDescriptions)
    if (filtersDescriptions.length) {
      return (
        <>
          <div className="mt-3 text-base sm:mt-0 sm:ml-4 sm:text-left">
            <p className="text-gray-500 mb-3">Se exportarán productos que cumplan con las siguientes condiciones: </p>
            <ul className="list-disc ml-6">
              {filtersDescriptions.map(description => (
                <li>{description}</li>
              ))}
            </ul>
          </div>
        </>
      )
    } else {
      return <p>{i18n.t('list_locations.export_all_products_description')}</p>
    }
  }

  const [exportDescription, setExportDescription] = useState(buildExportDescription(null))

  const queryEnabled = useMemo(() => {
    const filterParamKeys = Object.keys(searchParams).filter(
      key => searchParams[key] != null && searchParams[key] != '' && key !== 'page' && key !== 'per_page' && key !== 'order_by' && key !== 'order',
    )
    const enableQuery = filterParamKeys.length > 0
    if (!enableQuery) queryClient.resetQueries('warehouse_locations', { exact: false })
    return enableQuery
  }, [searchParams])

  const { isLoading, isError, error, data, isFetching, isPreviousData } = useQuery(
    ['warehouse_locations', searchParams, warehouse_id],
    () => fetchWarehouseLocations(warehouse_id, searchParams),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled: queryEnabled,
    },
  )

  // Container counts are fetched via a lightweight batch endpoint AFTER the list
  // renders (with only the visible page's pairs), so the heavy locations query is
  // never touched. Merged by `${location_id}:${product_id}`.
  const containerPairs = useMemo(() => {
    if (!data?.locations) return []
    return data.locations.filter(l => l.warehouse_location?.id && l.product?.id).map(l => ({ location_id: l.warehouse_location.id, product_id: l.product.id }))
  }, [data])

  const pairsKey = useMemo(() => containerPairs.map(p => `${p.location_id}:${p.product_id}`).join(','), [containerPairs])

  const { data: countsData } = useQuery(
    ['location_container_counts', warehouse_id, pairsKey],
    () => fetchLocationContainerCounts(warehouse_id, containerPairs),
    { enabled: containerPairs.length > 0, refetchOnWindowFocus: false, keepPreviousData: true },
  )

  const countsMap = useMemo(() => {
    const map = {}
    ;(countsData?.counts || []).forEach(c => {
      map[`${c.location_id}:${c.product_id}`] = c
    })
    return map
  }, [countsData])

  const onSortChange = orderBy => {
    // console.log("ON_ORDER_BY",orderBy)
    if (orderBy.length > 0) {
      setSearchParams({
        ...searchParams,
        order_by: orderBy[0].id,
        order: orderBy[0].desc ? 'desc' : 'asc',
      })
    } else {
      if ('order_by' in searchParams) {
        delete searchParams.order_by
        delete searchParams.order
        setSearchParams({ ...searchParams })
      }
    }
  }

  const onFilterChange = activeFilters => {
    // console.log("ON_CHANGE_FILTER", activeFilters)
    columns.forEach(col => {
      if (searchParams[col.accessor]) {
        delete searchParams[col.accessor]
      }
    })
    const filters = []
    activeFilters.forEach(filter => {
      filters[filter.id] = filter.value
    })
    tableRef.current.resetPagination()
    const mergedFilters = { ...searchParams, ...filters }
    setSearchParams({ ...mergedFilters, page: 1 })
    setExportDescription(buildExportDescription(mergedFilters))
    //console.log("searchParams",mergedFilters)
  }

  const renderStockByLot = stockByLot => {
    if (!stockByLot || stockByLot.length === 0) return null
    const headerClass = 'text-[10px] uppercase tracking-wide font-semibold text-gray-400 self-center'
    const dividerClass = 'bg-gray-200 self-stretch'
    return (
      <div className="grid gap-x-3 gap-y-1 text-xs" style={{ gridTemplateColumns: 'max-content 1px max-content 1px max-content' }}>
        <span className={headerClass}>{i18n.t('list_locations.lot_columns.lot')}</span>
        <div className={dividerClass} />
        <span className={headerClass}>{i18n.t('list_locations.lot_columns.qty')}</span>
        <div className={dividerClass} />
        <span className={headerClass}>{i18n.t('list_locations.lot_columns.expires')}</span>
        {stockByLot.map((lot, idx) => (
          <Fragment key={lot.lot_id ?? `no-lot-${idx}`}>
            {lot.lot_id === null ? (
              <span className="inline-flex justify-self-start self-center items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                {i18n.t('list_locations.no_lot')}
              </span>
            ) : (
              <span className="inline-flex justify-self-start self-center items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-mono whitespace-nowrap">
                {lot.unique_code}
              </span>
            )}
            <div className={dividerClass} />
            <span className="text-gray-700 font-medium tabular-nums self-center">{lot.count}</span>
            <div className={dividerClass} />
            <span className="text-gray-500 tabular-nums whitespace-nowrap self-center">
              {lot.expiration_date ? moment(lot.expiration_date).format('DD/MM/YYYY') : '—'}
            </span>
          </Fragment>
        ))}
      </div>
    )
  }

  const columns = [
    {
      Header: i18n.t('list_locations.headers.product_image_path'),
      accessor: 'product_image_path',
      disableFilters: true,
      disableSortBy: true,
    },
    {
      Header: i18n.t('list_locations.headers.location_code'),
      accessor: 'location_code',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t('list_locations.headers.store'),
      accessor: 'store',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t('list_locations.headers.location_type'),
      accessor: 'location_type',
      disableFilters: false,
      disableSortBy: true,
      Filter: cell => (
        <SelectColumnFilter
          column={cell.column}
          onSubmit={value => {
            cell.column.setFilter(value)
            const activeFilters = cell.columns.reduce((acc, column) => {
              if (column.id === cell.column.id) acc.push({ id: column.id, value: value })
              else if (column.filterValue) acc.push({ id: column.id, value: column.filterValue })
              return acc
            }, [])
            onFilterChange(activeFilters)
          }}
        />
      ),
      selectFilterOptions: LOCATION_TYPES.map(locationType => {
        return {
          id: locationType,
          label: locationType,
        }
      }),
    },
    {
      accessor: 'product_name',
      Header: i18n.t('list_locations.headers.product_name'),
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t('list_locations.headers.sku'),
      accessor: 'sku',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t('list_locations.headers.upc'),
      accessor: 'upc',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t('list_locations.headers.stock_total_count'),
      accessor: 'stock_total_count',
      disableFilters: true,
      disableSortBy: true,
      shrinkToContent: true,
    },
    {
      Header: i18n.t('list_locations.headers.stock_by_lot'),
      accessor: 'lot_code',
      disableFilters: false,
      disableSortBy: true,
      shrinkToContent: true,
    },
    {
      Header: i18n.t('list_locations.headers.actions'),
      fetchingIndicator: true,
      accessor: 'actions',
      disableFilters: true,
      disableSortBy: true,
      shrinkToContent: true,
    },
  ]

  const getMenuOptions = location => {
    const options = []
    return options
  }

  const getTableData = () => {
    if (!data?.locations) return []
    return data.locations.map(location => {
      const wl = location.warehouse_location
      const counts = wl?.id ? countsMap[`${wl.id}:${location.product.id}`] : null
      return {
        ...location,
        location_code: <div>{location.warehouse_location?.unique_code}</div>,
        location_type: <div>{location.warehouse_location?.location_type}</div>,
        store: <div>{location.product.store?.name}</div>,
        product_name: getName(location.product),
        sku: getSkus(location.product.product_codes),
        upc: <div>{location.product.bar_code}</div>,
        stock_total_count: (
          <div>
            <TotalCountContainer
              countByStatus={location.stock_count_by_status}
              total={location.stock_total_count}
              useStockOnlyFromConfirmedReplenishments={location.product.store?.requires_stock_only_from_confirmed_replenishments}
            />
            <ContainersCell
              counts={counts}
              t={i18n.t}
              onOpen={() =>
                setContainersPanel({
                  location_id: wl?.id,
                  product_id: location.product.id,
                  unique_code: wl?.unique_code,
                  location_type: wl?.location_type,
                })
              }
            />
          </div>
        ),
        lot_code: renderStockByLot(location.stock_by_lot),
        product_image_path: (
          <TableImage thumbnail_image={location.product.product_image_path?.thumbnail_path} regular_image={location.product.product_image_path?.path} />
        ),
        actions: <ActionMenu className="float-right" items={getMenuOptions(location)} />,
      }
    })
  }
  const handleExportDownload = function () {
    setExportNotificationOpen(false)
    setExportFileUrl(null)
    window.open(process.env.REACT_APP_PLATFORM_URL + exportFileUrl)
  }
  const exportNotificationBody = (
    <>
      <div className="w-0 flex-1 flex justify-between">
        {exportFileUrl && (
          <div className="flex-shrink-0">
            <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
          </div>
        )}
        <p className="my-0 ml-2 w-0 flex-1 text-sm font-medium text-gray-900">{!exportFileUrl ? 'Generando archivo' : 'Archivo generado'}</p>
        {!exportFileUrl && <p className="my-0 flex-shrink-0 text-base font-medium text-indigo-600">{exportProgress}%</p>}
        {exportFileUrl && (
          <button
            type="button"
            className="ml-3 flex-shrink-0 bg-white rounded-md text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={handleExportDownload}
          >
            {i18n.t('list_locations.download')}
          </button>
        )}
      </div>
    </>
  )

  const onConfirmExport = async function () {
    setEnqueueingExport(true)
    const { job_id } = await enqueueLocationsExport({
      ...searchParams,
      id: warehouse_id,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    setExportFileUrl(null)
    watchExportProgress(job_id)

    setExportDialogOpen(false)
    setExportNotificationOpen(true)
    setEnqueueingExport(false)
  }

  const watchExportProgress = function (job_id) {
    const intervalId = setInterval(async () => {
      const { progress_percentage: progressPercentage, download_url: downloadUrl } = await getLocationsExport(job_id)
      if (progressPercentage < 100) {
        setExportProgress(progressPercentage)
      } else {
        setExportFileUrl(downloadUrl)
        setExportProgress(0)
        clearInterval(intervalId)
      }
    }, 1000)
  }

  const handleLocationCreationSuccess = () => {
    // Optionally refresh the data or show a success message
    // The query will automatically refetch due to React Query's cache invalidation
    queryClient.invalidateQueries(['warehouse_locations'])
  }

  return (
    <>
      <ConfirmDialog
        open={exportDialogOpen}
        setOpen={value => setExportDialogOpen(value)}
        onCancel={() => setExportDialogOpen(false)}
        title={i18n.t('list_locations.export_inventory_title')}
        description={exportDescription}
        confirmLabel={i18n.t('list_locations.confirm')}
        cancelLabel={i18n.t('list_locations.cancel')}
        errorMessage={exportError}
        onConfirm={onConfirmExport}
        loading={enqueueingExport}
      />
      <Notification show={exportNotificationOpen} setShow={setExportNotificationOpen} customBody={exportNotificationBody} ephemeral={false} />
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {i18n.t('common.inventory')} {` `}
              <span className="text-sm text-gray-400">
                {i18n.t('list_locations.locations_by_product')} {` - `}
              </span>
              <span className="text-sm text-gray-400">
                {i18n.t('common.warehouse')} {` `} {warehouse_id}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {permissions.canPutawayToLocations && (
                <span>
                  <ButtonLoader className="whitespace-nowrap" type="secondary" onClick={() => setIsSimulatePutawayModalOpen(true)}>
                    <UploadIcon className="mr-1 -ml-1 h-4 w-4" aria-hidden="true" />
                    {i18n.t('simulate_putaway.button_label')}
                  </ButtonLoader>
                </span>
              )}

              <span>
                <ButtonLoader className="whitespace-nowrap" type="secondary" onClick={() => setExportDialogOpen(true)}>
                  <DownloadIcon className="mr-1 -ml-1 h-4 w-4" aria-hidden="true" />
                  {i18n.t('list_locations.export')}
                </ButtonLoader>
              </span>

              <span>
                <ButtonLoader
                  className="whitespace-nowrap"
                  type="secondary"
                  onClick={() => history.push(VIEW_WAREHOUSE_LOCATIONS.replace(':warehouse_id', warehouse_id))}
                >
                  {i18n.t('list_locations.view_locations')}
                </ButtonLoader>
              </span>

              {permissions.manageCycleCounts && (
                <span>
                  <ButtonLoader
                    className="whitespace-nowrap"
                    type="secondary"
                    onClick={() => history.push(CYCLE_COUNTS.replace(':warehouse_id', warehouse_id))}
                  >
                    {i18n.t('cycle_counts.title')}
                    <CoverageEventsBadge />
                  </ButtonLoader>
                </span>
              )}

              <FoundStockHubButton />

              {permissions.showInventoryHistory && (
                <span>
                  <ButtonLoader className="whitespace-nowrap" type={'secondary'} loading={loadingButton} disabled={loadingButton}>
                    <Link to={INVENTORY_HISTORY.replace(':warehouse_id', warehouse_id)} className="text-black hover:text-black">
                      {i18n.t('list_locations.inventory_history')}
                    </Link>
                  </ButtonLoader>
                </span>
              )}

              {permissions.deleteLostStock && (
                <span>
                  <ButtonLoader
                    className="whitespace-nowrap"
                    type="danger"
                    onClick={() => history.push(DELETE_LOST_STOCK.replace(':warehouse_id', warehouse_id))}
                  >
                    <TrashIcon className="mr-1 -ml-1 h-4 w-4" aria-hidden="true" />
                    {i18n.t('list_locations.delete_lost_stock')}
                  </ButtonLoader>
                </span>
              )}
            </div>
          </PageTopBar>
        }
      >
        {isError ? (
          <>Error: {error.message}</>
        ) : (
          <>
            {!isPreviousData && isLoading && <Loader show={true}></Loader>}
            <BasicTable
              onFilterChange={onFilterChange}
              onSortChange={onSortChange}
              columns={columns}
              showPaginator
              showHeader
              filterable
              sortable
              paginationMeta={data?.meta}
              labelToPaginator={i18n.t('common.locations')}
              pagesSize={searchParams.per_page}
              onPaginationChange={requestedPage => {
                setSearchParams({ ...searchParams, page: requestedPage })
              }}
              onPageSizeChange={pageSize => {
                setSearchParams({ ...searchParams, per_page: pageSize, page: 1 })
                dispatch(setLocationsListPageSize(pageSize))
                tableRef.current.resetPagination()
              }}
              showLoader={isFetching && isPreviousData}
              isFetching={isFetching}
              data={getTableData()}
              emptyTableText={searchParams.lot_code ? i18n.t('list_locations.empty_table_text_lot_code') : i18n.t('list_locations.empty_table_text')}
              queryOnClick
              ref={tableRef}
            />
          </>
        )}
      </PageView>

      <CreateLocationModal
        open={isCreateLocationModalOpen}
        setOpen={setIsCreateLocationModalOpen}
        warehouseId={warehouse_id}
        onSuccess={handleLocationCreationSuccess}
      />

      <SimulatePutawayModal open={isSimulatePutawayModalOpen} setOpen={setIsSimulatePutawayModalOpen} />

      <LocationContainersPanel open={!!containersPanel} onClose={() => setContainersPanel(null)} warehouseId={warehouse_id} location={containersPanel} />
    </>
  )
}
